import { Component } from '@angular/core';
import { paginationParams } from '@shared/config/table.config';
import { SearchService } from '@services/search.service';
import {
  performanceEvaluationData,
} from '@interfaces/vendor-management-model';
import { ErrorMessages } from '@constants/constants';
import { VendorService } from '@services/vendor.service';
import {
  PersonaType,
  Priority,
  RoutesEnum,
  PerformanceRating,
  PERFORMANCE_RATING_GRID_LABEL,
} from '@constants/global.enum';
import { ToasterService } from '@app/shared/services/toaster.service';
import { ExportService } from '@services/export.service';
import { dropdownOptions } from '@app/shared/interfaces/vendor.model';
import { columnDefs } from './performance-evalution-config';
import { PERFORMANCE_EVALUATION_CONSTANTS } from './performance-evalution.constants';

@Component({
  selector: 'app-performance-evalution',
  templateUrl: './performance-evalution.component.html',
  styleUrl: './performance-evalution.component.scss'
})
export class PerformanceEvalutionComponent  {
  selectedRowData: any = null;
  columnDefs = columnDefs;
  quarter: string = '';
  year: number = 0;
  selectedQuarters: {quarter: string, year: number}[] = [];
  paginationParams = paginationParams;
  data: performanceEvaluationData[] = [];
  displayData: performanceEvaluationData[] = [];
  originalData: performanceEvaluationData[] = [];
  allQuartersData: performanceEvaluationData[] = [];
  statusFilteredData: performanceEvaluationData[] = [];
  isStatusFilterApplied: boolean = false;
  filterCriteria: {behaviouralRating?: string, techincalRating?: string, performanceRating?: string} = {};
  filterValue: string = '';
  isDataLoaded: boolean = true;
  priority = Priority;
  opened: boolean = false;
  imageSrc: string = PERFORMANCE_EVALUATION_CONSTANTS.IMAGES.NO_DATA_FOUND;
  errorMsg: string = ErrorMessages.NoDataAvailable;
  selectedOption: string = '';
  quarterOptions: dropdownOptions[] = [];
  selectedRows: performanceEvaluationData[] = [];
  personaType = PersonaType;
  PERFORMANCE_EVALUATION_CONSTANTS = PERFORMANCE_EVALUATION_CONSTANTS;
  routesEnum = RoutesEnum;
  showContractorProfile = false;
  contractorId: string | null = null;

  constructor(
    private searchService: SearchService,
    private vendorService: VendorService,
    private toasterService: ToasterService,
    private exportService: ExportService,
  ) {}

  ngOnInit() {
    this.generateQuarterOptions();
    if (this.quarter && this.year) {
      this.selectedQuarters = [{ quarter: this.quarter, year: this.year }];
    }
    this.vendorService.currentVendor$.subscribe({
      next: (vendor) => {
        if (vendor) {
          this.getPerformanceOnQuartersDetails();
        }
      },
    });
  }

  selectedyear: number = new Date().getFullYear();
  selectedmonth: any = new Date().getMonth() + 1;

  onQuarterSelect(event: any): void {
    if (!event) {
      this.selectedQuarters = [];
      this.displayData = [];
      this.data = [];
      return;
    }

    this.selectedQuarters = [];
    let quarterStrings: string[] = [];
    
    if (typeof event === 'string') {
      quarterStrings = event.split(',').map((s: string) => s.trim());
    } else if (Array.isArray(event)) {
      quarterStrings = event.map((item: any) => item.value || item);
    } else if (event.value) {

      quarterStrings = [event.value];
    } else {
      quarterStrings = [event];
    }
    quarterStrings.forEach((quarterStr: string) => {
      const match = quarterStr.match(/Q(\d)\s+(\d{4})/);
      if (match) {
        this.selectedQuarters.push({
          quarter: `Q${match[1]}`,
          year: parseInt(match[2])
        });
      }
    });
    
    if (this.selectedQuarters.length > 0) {
      this.filterDataBySelectedQuarters();
    } else {
      this.displayData = [];
      this.data = [];
    }
  }

  generateQuarterOptions(): void {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentQuarter = Math.ceil(currentMonth / 3);
    
    this.year = currentYear;
    this.quarter = `Q${currentQuarter}`;
    
    this.quarterOptions = Array.from({ length: 4 }, (_, i) => {
      let quarter = currentQuarter - i;
      let year = currentYear;
    
      while (quarter <= 0) {
        quarter += 4;
        year -= 1;
      }
      
      const quarterLabel = `Q${quarter} ${year}`;
      
      return {
        label: quarterLabel,
        value: quarterLabel,
        selected: i === 0,
      };
    });
  }

  private fetchAndFilterData(): void {
    this.vendorService.getPerformanceOnQuartersDetails(this.quarter, this.year).subscribe({
      next: (res: any) => {
        if (res && res?.data && res?.data?.length > 0) {
          this.allQuartersData = res?.data;
          this.filterDataBySelectedQuarters();
        } else {
     
          this.allQuartersData = [];
          this.displayData = [];
          this.data = [];
          this.updateDataLoadedStatus([]);
        }
      },
      error: (error) => {
        console.error('API Error:', error);
        this.toasterService.showError(PERFORMANCE_EVALUATION_CONSTANTS.MESSAGES.FAILED_LOAD_TIMESHEET, PERFORMANCE_EVALUATION_CONSTANTS.TITLES.ERROR);
        this.allQuartersData = [];
        this.displayData = [];
        this.data = [];
        this.isDataLoaded = false;
        this.errorMsg = ErrorMessages.NoDataAvailable;
      },
    });
  }

  private filterDataBySelectedQuarters(): void {
    if (this.allQuartersData.length === 0) {
      return;
    }

    const filteredData = this.allQuartersData.filter((item: performanceEvaluationData) => {
      const matchFound = this.selectedQuarters.some(selected => {
        const quarterMatch = item.quarter === selected.quarter;
        const yearMatch = item.year === selected.year;
        return quarterMatch && yearMatch;
      });
      return matchFound;
    });

    if (filteredData.length > 0) {
      this.processPerformanceData(filteredData);
    } else {
      this.displayData = [];
      this.data = [];
      this.updateDataLoadedStatus([]);
    }
  }

  getPerformanceOnQuartersDetails(): void {
    this.fetchAndFilterData();
  }

  private processPerformanceData(rawData: performanceEvaluationData[]): void {
    this.originalData = rawData.map((item) => {
      const processedItem = { ...item };
      // Translate the raw enum ('A' / 'B' / 'TOO_NEW' / 'N_A') into a
      // human-friendly grid label (e.g. "B — Solid Contributor",
      // "Too New to Rate", "Not Applicable") for direct rendering.
      const raw = processedItem.performanceRating;
      if (raw) {
        const key = raw as PerformanceRating;
        processedItem.performanceRating =
          PERFORMANCE_RATING_GRID_LABEL[key] ?? raw;
      }
      return processedItem;
    });
    this.data = [...this.originalData];
    this.displayData = [...this.originalData];
    if (this.isStatusFilterApplied && (this.filterCriteria.behaviouralRating || this.filterCriteria.techincalRating)) {
      this.reapplyFilterCriteria();
    }
    this.applyCurrentFilters();
    this.selectedRows = [];
  }

  private applyCurrentFilters(): void {
    let baseData = [...this.originalData];
    if (this.isStatusFilterApplied) {
      baseData = [...this.statusFilteredData];
    }

    if (this.filterValue && this.filterValue.trim()) {
      const searchValue = this.filterValue.toLowerCase();
      const numberSearch = !isNaN(Number(searchValue));
      const searchableFields = ['employeeName', 'employeeId', 'vendorName', 'behaviouralRating', 'techincalRating', 'quarter', 'year'];

      baseData = baseData.filter(item => {
        if (numberSearch) {
          return searchableFields.some(field => 
            (item as any)[field]?.toString() === searchValue
          );
        } else {
          return searchableFields.some(field =>
            (item as any)[field]?.toString().toLowerCase().includes(searchValue)
          );
        }
      });
    }

    this.displayData = [...baseData];
    this.updateDataLoadedStatus(this.displayData);
  }

  resetFilter(data: performanceEvaluationData[]): void {
    this.isStatusFilterApplied = false;
    this.statusFilteredData = [];
    this.filterCriteria = {};
    this.applyCurrentFilters();
  }

  applyFilter(filterData: performanceEvaluationData[]): void {
    this.isStatusFilterApplied = true;
    this.statusFilteredData = filterData;
    this.opened = false;
    this.applyCurrentFilters();
  }

  clearInput() {
    this.filterValue = '';
    this.applyCurrentFilters();
  }

  searchPerformanceRecord(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event as KeyboardEvent);
    this.applyCurrentFilters();
  }

  onStatusSelect(filteredData: performanceEvaluationData[]): void {
    this.isStatusFilterApplied = true;
    this.statusFilteredData = filteredData;
    this.applyCurrentFilters();
  }

  onFilterCriteriaReceived(criteria: {behaviouralRating?: string, techincalRating?: string, performanceRating?: string}): void {
    this.filterCriteria = criteria;
    this.isStatusFilterApplied = true;
  }

  private reapplyFilterCriteria(): void {
    const filters: [keyof performanceEvaluationData, any, (a: any, b: any) => boolean][] = [
      ['behaviouralRating', this.filterCriteria.behaviouralRating, (a, b) => a === b],
      ['techincalRating', this.filterCriteria.techincalRating, (a, b) => a === b],
      ['performanceRating', this.filterCriteria.performanceRating, (a, b) => a === b],
    ];

    this.statusFilteredData = filters.reduce(
      (data, [key, value, comparator]) =>
        value ? data.filter((item) => comparator(item[key], value)) : data,
      this.originalData,
    );
  }

  isRowSelected(row: performanceEvaluationData): boolean {
    return this.selectedRows.some(selectedRow => 
      selectedRow.employeeId === row.employeeId && 
      selectedRow.quarter === row.quarter && 
      selectedRow.year === row.year
    );
  }

  onCheckboxChange(row: performanceEvaluationData, checked: any): void {
    const isChecked = typeof checked === 'boolean' ? checked : checked?.target?.checked || false;
    row.selected = isChecked;

    if (isChecked) {
      if (!this.isRowSelected(row)) {
        this.selectedRows.push(row);
      }
    } else {
      this.selectedRows = this.selectedRows.filter(selectedRow =>
        !(selectedRow.employeeId === row.employeeId && 
          selectedRow.quarter === row.quarter && 
          selectedRow.year === row.year)
      );
    }
  }

  onRowSelected(selectedRows: performanceEvaluationData[]): void {
    this.selectedRows = selectedRows;
  }

  private updateDataLoadedStatus(data: performanceEvaluationData[]): void {
    this.isDataLoaded = true;
    if (!data || data.length === 0) {
      this.errorMsg = ErrorMessages.NoDataAvailable;
    }
  }

  navigateToContractorProfile(empId: string) {
    this.contractorId = empId;
    this.showContractorProfile = true;
  }

  download(): void {
    const dataWithQuarterYear = this.selectedRows.map(row => ({
      ...row,
      quarterYear: `${row.quarter} ${row.year}`
    }));
    const columnsWithoutCalendar = this.columnDefs.filter(
      (col: any) => col.field !== 'select' && col.field !== 'employeeId' && col.field !== 'vendorId',
    );
    this.exportService.handleExport(
      dataWithQuarterYear,
      columnsWithoutCalendar,
      'performance-evaluation',
    );
  }
}
